import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { getWorkspaceFormConfig, WORKSPACE_FORM_SCHEMA } from '@constants';
import { ApiEndpoint, FormType, ServiceModalName, Translation, WorkspaceMemberEndpoint } from '@enums';
import { useAppDispatch, useCreateModal } from '@hooks';
import { submitFormRequest } from '@store';
import i18n from '@translations';
import type { FormConfig, WorkspaceConfigItem, WorkspaceMemberForm, WsConfigOwnershipProps } from '@types';
import { convertOptionList, getOwnerDetails } from '@utils';

import { WsConfigCard } from '../components';

export const WsConfigOwnership: React.FC<WsConfigOwnershipProps> = ({ workspaceMember, isOwner = false }) => {
  const { id: application_id } = useParams();
  const dispatch = useAppDispatch();

  const formType = FormType.Workspace;
  const schema = WORKSPACE_FORM_SCHEMA;

  const { t: tForms } = useTranslation(Translation.Workspace, { keyPrefix: 'forms' });
  const { t: tTabs } = useTranslation(Translation.Workspace, { keyPrefix: 'tabs' });
  const { t: tButtons } = useTranslation(Translation.Workspace, { keyPrefix: 'buttons' });

  const ownerShipDetails = useMemo(() => getOwnerDetails(workspaceMember), [workspaceMember]);

  const filteredOptions = useMemo(() => {
    return workspaceMember?.filter((option) => option.status === 'active');
  }, [workspaceMember]);

  const { options: ownerOptions } = useMemo(
    () =>
      convertOptionList({
        originalOptions: filteredOptions ?? [],
        defaultOptions: '',
        output: { value: 'email', label: 'name' },
      }),
    [filteredOptions, tForms],
  );

  const configItems: WorkspaceConfigItem[] = [
    {
      left: tForms('ownerName'),
      right: ownerShipDetails?.name,
    },
    {
      left: tForms('ownerEmail'),
      right: ownerShipDetails?.email,
    },
  ];

  const formConfig: FormConfig = useMemo(
    () =>
      getWorkspaceFormConfig({
        endpoint: WorkspaceMemberEndpoint.TRANSFER,
        options: { ownerOptions },
        schema,
      }),
    [ownerOptions, schema, i18n.language],
  );

  const initialValues: WorkspaceMemberForm = useMemo(
    () => ({
      email: ownerShipDetails?.email,
    }),
    [ownerShipDetails],
  );

  const handleSubmit = useCallback(
    (values: WorkspaceMemberForm) => {
      dispatch(
        submitFormRequest({
          formType,
          formData: values,
          params: {
            content_id: application_id,
          },
          globalOptions: {
            returnResult: true,
            content: true,
            endPath: true,
            returnPath: ApiEndpoint.CONFIGURATION,
            endpoint: WorkspaceMemberEndpoint.TRANSFER,
          },
        }),
      );
    },
    [application_id, formType, dispatch],
  );

  const { openModal, isSubmitting } = useCreateModal({
    modalName: ServiceModalName.FormModal,
    formType,
    formConfig,
    initialValues,
    handleSubmit,
  });

  return (
    <WsConfigCard
      label={{ title: tTabs('workspaceOwner'), confirmButton: tButtons('transferOwnership') }}
      infoItems={configItems}
      isSubmitting={isSubmitting}
      callbackFunction={{
        ...(isOwner && { click: openModal }),
      }}
    />
  );
};
